import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Label,
  LoadingButton,
  Stack,
  toast,
} from "@altimateai/lego";
import { getIconByDatastoreType } from "../../DatastoreImage";
import { ConnectionProps } from "./types";
import {
  AzureDatabricksPrivateLinkConnectionRequest,
  DatastoreTypeEnum,
} from "@/pages/datastores/types";
import { saveAzureDatabricksPrivateLinkConnection } from "@/helpers/apis";
import { showAzureDatabricksConnections } from "@/helpers/tenant";

const formSchema = z.object({
  workspace_resource_id: z.string().min(1, "Workspace Resource ID is required"),
  workspace_url: z.string().min(1, "Workspace URL is required"),
  storage_account_resource_id: z.string().min(1, "Storage Account Resource ID is required"),
  storage_account_fqdn: z.string().min(1, "Storage Account FQDN is required"),
  azure_region: z.string().min(1, "Azure Region is required"),
});

type FormValues = z.infer<typeof formSchema>;

const AzureDatabricksPrivateLinkConnection = ({
  typeData,
  handleBack,
}: ConnectionProps): JSX.Element => {
  const navigate = useNavigate();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workspace_resource_id: "",
      workspace_url: "",
      storage_account_resource_id: "",
      storage_account_fqdn: "",
      azure_region: "",
    },
    mode: "onBlur",
  });

  const { mutate: saveConnection, isPending: isSaving } = useMutation({
    mutationFn: (data: AzureDatabricksPrivateLinkConnectionRequest) =>
      saveAzureDatabricksPrivateLinkConnection(data),
    onSuccess: () => {
      toast({
        title: "Saved",
        description: "Azure Databricks Private Link connection saved successfully",
      });
      navigate("/settings/datastores");
    },
    onError: (err: unknown) => {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          (err as Error)?.message || "Unable to save Azure Databricks Private Link connection",
      });
    },
  });

  const onSubmit = (values: FormValues) => {
    const payload: AzureDatabricksPrivateLinkConnectionRequest = {
      name: typeData.name,
      description: typeData.description,
      ...values,
    };
    saveConnection(payload);
  };

  const showTestConnection = !showAzureDatabricksConnections();

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justify="end" className="al-items-center al-mt-2">
          <Stack direction="row" className="al-items-center al-gap-2">
            {getIconByDatastoreType({
              datastoreType: DatastoreTypeEnum.AZURE_DATABRICKS_PRIVATE_LINK,
              className: "al-w-8 al-h-8",
            })}
            <span className="al-text-base al-font-medium">Azure Databricks Private Link</span>
          </Stack>
        </Stack>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="al-mt-6">
            <Stack gap="xl" direction="column">
              <FormField
                control={form.control}
                name="workspace_resource_id"
                render={({ field }) => (
                  <FormItem>
                    <Label className="al-block" htmlFor="workspace_resource_id">
                      Databricks Workspace Resource ID
                    </Label>
                    <FormControl>
                      <Input
                        id="workspace_resource_id"
                        placeholder="/subscriptions/<SUB_ID>/resourceGroups/<RG>/providers/Microsoft.Databricks/workspaces/<NAME>"
                        className="al-mt-1"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="workspace_url"
                render={({ field }) => (
                  <FormItem>
                    <Label className="al-block" htmlFor="workspace_url">
                      Databricks Workspace URL
                    </Label>
                    <FormControl>
                      <Input
                        id="workspace_url"
                        placeholder="adb-1234567890123456.11.azuredatabricks.net"
                        className="al-mt-1"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="storage_account_resource_id"
                render={({ field }) => (
                  <FormItem>
                    <Label className="al-block" htmlFor="storage_account_resource_id">
                      Storage Account Resource ID
                    </Label>
                    <FormControl>
                      <Input
                        id="storage_account_resource_id"
                        placeholder="/subscriptions/<SUB_ID>/resourceGroups/<RG>/providers/Microsoft.Storage/storageAccounts/<NAME>"
                        className="al-mt-1"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="storage_account_fqdn"
                render={({ field }) => (
                  <FormItem>
                    <Label className="al-block" htmlFor="storage_account_fqdn">
                      Storage Account FQDN
                    </Label>
                    <FormControl>
                      <Input
                        id="storage_account_fqdn"
                        placeholder="mystorageaccount.blob.core.windows.net"
                        className="al-mt-1"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="azure_region"
                render={({ field }) => (
                  <FormItem>
                    <Label className="al-block" htmlFor="azure_region">
                      Azure Region
                    </Label>
                    <FormControl>
                      <Input
                        id="azure_region"
                        placeholder="East US"
                        className="al-mt-1"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Stack direction="row" justify="between">
                <Button variant="outline" asChild>
                  <Link to="/settings/datastores">Cancel</Link>
                </Button>
                <Stack gap="md">
                  <Button variant="secondary" onClick={handleBack}>
                    Back
                  </Button>
                  {showTestConnection && (
                    <Button variant="default" disabled>
                      Test Connection
                    </Button>
                  )}
                  <LoadingButton
                    variant="default"
                    type="submit"
                    loading={isSaving}
                    className="al-px-4"
                    disabled={!form.formState.isValid}
                  >
                    Save Connection
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default AzureDatabricksPrivateLinkConnection;
